/**
 * The message history.
 *
 * Three things here are load-bearing and easy to get wrong:
 *
 * 1. Scroll anchoring. Older messages are prepended above the viewport, which
 *    moves everything the user is reading down the page. The height is measured
 *    before the prepend and the scroll position corrected after it, so the eye
 *    stays on the same line.
 * 2. Tombstones. A deleted message keeps its place and renders a placeholder.
 *    The room's sequence is gap-free by contract, and a client that removed the
 *    row would be showing a hole that does not exist.
 * 3. Text is text. Message bodies, display names and webhook sender names are
 *    all attacker-controlled and are only ever rendered as React elements.
 */

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, UIEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import { isOptimisticMessage, markRoomRead, refreshCachedMessage, useRoomMessages } from '@/api/store'
import type { PendingMessage } from '@/api/store'
import { unreadOf } from '@/api/types'
import type { Message, Room, User } from '@/api/types'
import { UserNameButton } from '@/components/UserCard'
import { Avatar, Badge, Button, EmptyState, IconButton, Spinner, useToast } from '@/components/ui'
import { AttachmentList } from '@/components/AttachmentView'
import { Composer } from '@/components/Composer'
import { AddReactionButton, MessageReactions } from '@/components/MessageReactions'
import { WebhookBlocks } from '@/components/WebhookBlocks'
import { GiphyMessage } from '@/components/Giphy'
import { LinkPreviews } from '@/components/LinkPreviews'
import { gifId, gifMessageParts } from '@/api/giphy'
import { formatDay, formatTime } from '@/lib/format'
import { renderMarkdown } from '@/lib/markdown'
import { useAppLayout } from '@/routes/AppShell'
import styles from './MessageList.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

/** Messages closer together than this from one person share a header. */
const GROUP_WINDOW_MS = 5 * 60 * 1000
/** How near the top counts as "reading the oldest thing loaded". */
const LOAD_OLDER_THRESHOLD_PX = 160
/** How near the bottom counts as "following the conversation". */
const AT_BOTTOM_THRESHOLD_PX = 80

function personName(user: User): string {
  const display = user.display_name.trim()
  return display === '' ? user.username : display
}

function senderLabel(message: Message): string {
  const override = message.sender_override?.trim() ?? ''
  return override === '' ? personName(message.sender) : override
}

/** First name on narrow layouts; full label stays in title. */
function isRootMessage(message: Message): boolean {
  return message.parent_id == null || message.parent_id === ''
}

/** How many faces a thread pill shows before it stops counting. */
const THREAD_FACES = 3

interface ThreadSummary {
  count: number
  /** The people who replied, oldest first, for the faces on the pill. */
  participants: User[]
  /** When the newest loaded reply arrived, or null when none are loaded. */
  lastAt: string | null
}

const NO_THREAD: ThreadSummary = { count: 0, participants: [], lastAt: null }

/**
 * A summary per thread root, from one pass over the room.
 *
 * Built once for the whole list rather than per row: a room with a few hundred
 * messages loaded would otherwise be scanned again for every message drawn.
 */
function threadSummaries(messages: readonly Message[]): Map<string, ThreadSummary> {
  const loaded = new Map<string, { count: number; participants: User[]; lastAt: string | null }>()

  for (const message of messages) {
    const root = message.parent_id
    if (root === null || root === undefined || root === '') continue
    if (message.deleted_at !== null) continue

    const current = loaded.get(root) ?? { count: 0, participants: [], lastAt: null }
    if (
      current.participants.length < THREAD_FACES &&
      !current.participants.some((person) => person.id === message.sender.id)
    ) {
      current.participants.push(message.sender)
    }
    current.count += 1
    // The list is in room order, so the last one seen is the newest.
    current.lastAt = message.created_at
    loaded.set(root, current)
  }

  const summaries = new Map<string, ThreadSummary>()

  for (const message of messages) {
    if (!isRootMessage(message)) continue

    const here = loaded.get(message.id)
    // The server's count is authoritative; the replies loaded here are only
    // what this client happens to have, which is fewer until the thread has
    // been opened.
    const count = Math.max(here?.count ?? 0, message.reply_count ?? 0)
    if (count === 0) continue

    summaries.set(message.id, {
      count,
      participants: here?.participants ?? [],
      lastAt: here?.lastAt ?? null,
    })
  }

  return summaries
}

function describeFailure(error: unknown): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return 'The server could not be reached.'
}

// ── Grouping ────────────────────────────────────────────────────────────────

interface SenderGroup {
  key: string
  messages: Message[]
}

interface DayGroup {
  key: string
  label: string
  groups: SenderGroup[]
}

function buildDays(messages: readonly Message[]): DayGroup[] {
  const days: DayGroup[] = []
  let day: DayGroup | null = null
  let group: SenderGroup | null = null
  let previous: Message | null = null

  for (const message of messages) {
    const at = new Date(message.created_at).getTime()
    const dayKey = Number.isNaN(at) ? 'undated' : new Date(at).toDateString()

    if (day === null || day.key !== dayKey) {
      day = { key: dayKey, label: formatDay(message.created_at), groups: [] }
      days.push(day)
      group = null
    }

    const sameSender =
      previous !== null &&
      previous.sender.id === message.sender.id &&
      previous.sender_override === message.sender_override &&
      previous.sender_avatar_url === message.sender_avatar_url

    const closeInTime =
      previous !== null &&
      !Number.isNaN(at) &&
      at - new Date(previous.created_at).getTime() < GROUP_WINDOW_MS

    if (group === null || !sameSender || !closeInTime) {
      group = { key: message.id, messages: [] }
      day.groups.push(group)
    }

    group.messages.push(message)
    previous = message
  }

  return days
}

// ── One message ─────────────────────────────────────────────────────────────

interface MessageRowProps {
  idPrefix?: string
  message: Message
  room: Room
  /** True on a layout with no pointer to hover with. */
  compact: boolean
  selfId: string | null
  canModerate: boolean
  showStamp: boolean
  highlighted: boolean
  delivery: PendingMessage | undefined
  editing: boolean
  onEdit: () => void
  onEditDone: () => void
  onRetry: (clientMessageId: string) => void
  onDiscard: (clientMessageId: string) => void
  onReply?: () => void
  thread: ThreadSummary
  onOpenThread?: () => void
}

export function MessageRow({
  idPrefix = '',
  message,
  room,
  compact,
  selfId,
  canModerate,
  showStamp,
  highlighted,
  delivery,
  editing,
  onEdit,
  onEditDone,
  onRetry,
  onDiscard,
  onReply,
  thread,
  onOpenThread,
}: MessageRowProps): ReactElement {
  const toast = useToast()
  const { server, user } = useAuth()
  const [confirming, setConfirming] = useState(false)
  const [copied, setCopied] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const moreRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!moreOpen) return
    const close = (event: PointerEvent): void => {
      if (event.target instanceof Node && !moreRef.current?.contains(event.target)) setMoreOpen(false)
    }
    const escape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMoreOpen(false)
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [moreOpen])

  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 2000)
    return () => window.clearTimeout(timer)
  }, [copied])

  const optimistic = isOptimisticMessage(message)
  const isOwn = message.sender.id === selfId
  const deleted = message.deleted_at !== null

  const editWindow = server?.message_edit_window_seconds ?? null
  const withinEditWindow =
    editWindow === null ||
    Date.now() - new Date(message.created_at).getTime() <= editWindow * 1000

  const canEdit =
    isOwn &&
    !deleted &&
    !optimistic &&
    server?.message_editing_enabled !== false &&
    withinEditWindow
  const canDelete =
    (canModerate || (isOwn && server?.message_deleting_enabled !== false)) && !deleted && !optimistic
  const canReply = onReply !== undefined && !deleted && !optimistic

  const saveEdit = useCallback(
    async (text: string): Promise<void> => {
      const updated = await api.patch<Message>(`/messages/${encodeURIComponent(message.id)}`, { text })
      refreshCachedMessage(updated)
      onEditDone()
    },
    [message.id, onEditDone],
  )

  const remove = useCallback(async (): Promise<void> => {
    setDeleting(true)
    try {
      await api.del<void>(`/messages/${encodeURIComponent(message.id)}`)
      setConfirming(false)
    } catch (error) {
      toast.error(describeFailure(error))
    } finally {
      setDeleting(false)
    }
  }, [message.id, toast])

  const copyLink = useCallback((): void => {
    const url = `${window.location.origin}/rooms/${room.id}?message=${message.id}`
    // Typed as optional deliberately: a page served over plain HTTP has no
    // clipboard at all, and a relaii server on a LAN address is a normal
    // deployment.
    const clipboard: Clipboard | undefined = navigator.clipboard
    if (clipboard === undefined) {
      toast.error('This browser will not let the page copy to the clipboard.')
      return
    }
    clipboard.writeText(url).then(
      () => setCopied(true),
      () => toast.error('The link could not be copied.'),
    )
  }, [message.id, room.id, toast])

  const failed = delivery?.status === 'failed'
  const wasEdited = message.edited_at !== null && !deleted

  return (
    <div
      className={cx(
        styles.row,
        wasEdited && styles.editedRow,
        moreOpen && styles.menuOpen,
        highlighted && styles.highlighted,
        delivery?.status === 'sending' && styles.pending,
        failed && styles.failedRow,
      )}
      id={`${idPrefix}message-${message.id}`}
    >
      <div className={cx(styles.messageMeta, showStamp && !wasEdited && styles.hoverMeta)}>
        <time dateTime={message.created_at}>{formatTime(message.created_at)}</time>
        {wasEdited ? (
          <span className={styles.edited} title={message.edited_at ?? undefined}>(edited)</span>
        ) : null}
      </div>

      {editing ? (
        <div className={styles.editor}>
          <Composer
            roomId={room.id}
            edit={{ id: message.id, text: message.text, onSave: saveEdit, onCancel: onEditDone }}
          />
        </div>
      ) : deleted ? (
        <p className={styles.deleted}>This message was deleted.</p>
      ) : (
        <div className={styles.text}>
          {(message.blocks ?? []).length > 0 ? (
            <WebhookBlocks
              blocks={message.blocks ?? []}
              currentUsername={user?.qualified_id ?? null}
              mentionsEveryone={message.mentions_everyone}
              keyPrefix={message.id}
            />
          ) : gifMessageParts(message.text).map((part, index) => gifId(part) ? <GiphyMessage key={`${message.id}-gif-${index}-${part}`} text={part} /> : (
            renderMarkdown(part, {
              // The qualified id covers both `@chris` and `@chris@server`, so a
              // mention of the reader is drawn as one wherever it came from.
              currentUsername: user?.qualified_id ?? null,
              mentionsEveryone: message.mentions_everyone,
              keyPrefix: `${message.id}-${index}`,
            })
          ))}
        </div>
      )}

      {!editing && !deleted && !optimistic ? <LinkPreviews messageId={message.id} text={message.text} /> : null}

      {!editing && message.attachments.length > 0 ? (
        <AttachmentList attachments={message.attachments} />
      ) : null}

      {/* The action row carries the one "add" control, on every layout. */}
      {!editing && !deleted ? (
        <MessageReactions message={message} disabled={optimistic} showAdd={false} />
      ) : null}

      {!editing && !deleted && thread.count > 0 && onOpenThread !== undefined ? (
        <button type="button" className={styles.threadPreview} onClick={onOpenThread}>
          {thread.participants.length > 0 ? (
            <span className={styles.threadFaces} aria-hidden="true">
              {thread.participants.map((person) => (
                <Avatar
                  key={person.id}
                  className={styles.threadFace}
                  name={personName(person)}
                  id={person.id}
                  src={person.avatar_url}
                  size="xs"
                  decorative
                />
              ))}
            </span>
          ) : null}
          <span className={styles.threadCount}>
            {thread.count === 1 ? '1 reply' : `${thread.count} replies`}
          </span>
          {thread.lastAt !== null ? (
            <span className={styles.threadLast}>{`last ${formatTime(thread.lastAt)}`}</span>
          ) : null}
        </button>
      ) : null}

      {failed && delivery !== undefined ? (
        <p className={styles.retry} role="alert">
          <span className={styles.notSent}>Not sent</span>
          <span className={styles.notSentWhy}>{delivery.error ?? ''}</span>
          <Button size="sm" onClick={() => onRetry(delivery.client_message_id)}>
            Try again
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDiscard(delivery.client_message_id)}>
            Delete
          </Button>
        </p>
      ) : null}

      {/*
        React, start a thread, and everything else behind "More" — the order
        the sketches settled on. Editing and deleting are rare next to those
        two and are one press further away rather than sitting under the
        pointer next to them.
      */}
      {!editing && !deleted && !optimistic ? (
        <div className={cx(styles.actions, compact && styles.actionsPinned)}>
          {confirming ? (
            <>
              <span className={styles.confirm}>Delete this message?</span>
              <Button size="sm" variant="danger" loading={deleting} onClick={() => void remove()}>
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                Keep
              </Button>
            </>
          ) : (
            <>
              {copied ? <span className={styles.copied}>Link copied</span> : null}
              <AddReactionButton message={message} />
              {canReply ? (
                <IconButton label="Reply in thread" size="sm" onClick={onReply}>
                  <ReplyIcon />
                </IconButton>
              ) : null}
              <div className={styles.more} ref={moreRef}>
                <IconButton
                  label="More actions"
                  size="sm"
                  pressed={moreOpen}
                  aria-expanded={moreOpen}
                  aria-haspopup="menu"
                  onClick={() => setMoreOpen((open) => !open)}
                >
                  <MoreIcon />
                </IconButton>
                {moreOpen ? (
                  <div className={styles.moreMenu} role="menu" aria-label="Message actions">
                    {server?.capabilities?.saved_messages === true ? <button type="button" role="menuitem" onClick={() => {
                      setMoreOpen(false)
                      void api.post(`/messages/${encodeURIComponent(message.id)}/saved`).then(
                        () => toast.success('Saved for later.'),
                        () => toast.error('The message could not be saved for later. Try again.'),
                      )
                    }}>Save for later</button> : null}
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMoreOpen(false)
                        copyLink()
                      }}
                    >
                      <CopyLinkIcon />
                      Copy link to message
                    </button>
                    {canEdit ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMoreOpen(false)
                          onEdit()
                        }}
                      >
                        <EditIcon />
                        Edit message
                      </button>
                    ) : null}
                    {canDelete ? (
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.moreDanger}
                        onClick={() => {
                          setMoreOpen(false)
                          setConfirming(true)
                        }}
                      >
                        <DeleteIcon />
                        Delete message
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}

// ── Icons ───────────────────────────────────────────────────────────────────

function ReplyIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M3.5 8H12a2.5 2.5 0 0 1 0 5H9.5M3.5 8 6 5.5M3.5 8 6 10.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function MoreIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true" focusable="false">
      <circle cx="3.5" cy="8" r="1.3" />
      <circle cx="8" cy="8" r="1.3" />
      <circle cx="12.5" cy="8" r="1.3" />
    </svg>
  )
}

function CopyLinkIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="m6.1 10.4-1.35 1.35a2.65 2.65 0 0 1-3.75-3.75l2-2a2.65 2.65 0 0 1 3.75 0M9.9 5.6l1.35-1.35A2.65 2.65 0 1 1 15 8l-2 2a2.65 2.65 0 0 1-3.75 0M5.5 10.5l5-5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

function EditIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M9.9 3.1 12.9 6.1M3 13l.7-3.2 7.6-7.6a1.35 1.35 0 0 1 1.9 0l.6.6a1.35 1.35 0 0 1 0 1.9l-7.6 7.6L3 13Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function DeleteIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M3 4.5h10M6 4.5V2.8h4v1.7M4.4 4.5l.55 9h6.1l.55-9M6.7 7v4M9.3 7v4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}




// ── The list ────────────────────────────────────────────────────────────────

export interface MessageListProps {
  room: Room
  /** Shown in the accessible name of the scroll region. */
  roomName: string
  onOpenThread?: (rootId: string) => void
  onEditMessage?: (message: Message) => void
}

export function MessageList({ room, roomName, onOpenThread, onEditMessage }: MessageListProps): ReactElement {
  const layout = useAppLayout()
  const avatarSize = layout.compact ? 'md' : 'sm'
  const { user } = useAuth()
  const { messages, pending, hasMore, loading, error, loadOlder, retry, discard } = useRoomMessages(
    room.id,
  )
  const [searchParams] = useSearchParams()
  const highlightId = searchParams.get('message')
  const [jumpPages, setJumpPages] = useState(10)
  const jumpTarget = messages.find((message) => message.id === highlightId)
  const scrolledTarget = useRef<string | null>(null)

  useEffect(() => {
    setJumpPages(10)
    scrolledTarget.current = null
  }, [highlightId, room.id])

  useEffect(() => {
    if (highlightId === null || loading) return
    if (jumpTarget !== undefined) {
      if (!isRootMessage(jumpTarget) || scrolledTarget.current === highlightId) return
      const element = document.getElementById(`message-${highlightId}`)
      if (element !== null) {
        followingRef.current = false
        setFollowing(false)
        element.scrollIntoView({ block: 'center' })
        scrolledTarget.current = highlightId
      }
    } else if (hasMore && error === null && jumpPages > 0) {
      setJumpPages((remaining) => remaining - 1)
      loadOlder()
    }
  }, [highlightId, jumpTarget, loading, hasMore, error, jumpPages, loadOlder])

  const scrollRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef({ scrollHeight: 0, scrollTop: 0, firstSequence: null as number | null })
  const followingRef = useRef(true)
  const announcedRef = useRef<string | null>(null)
  const roomRef = useRef(room.id)
  const initialScrollRoomRef = useRef<string | null>(null)

  const [following, setFollowing] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState('')
  /**
   * Where the "New messages" line sits: the last sequence this account had
   * read when the room was opened. Frozen on purpose — reading marks the room
   * read within a second, and a line that moved with it would vanish before
   * the eye reached it.
   */
  const [unreadMark, setUnreadMark] = useState<number | null>(null)

  const selfId = user?.id ?? null
  const canModerate =
    room.my_membership?.role === 'owner' ||
    room.my_membership?.role === 'moderator' ||
    user?.is_admin === true

  const days = useMemo(
    () => buildDays(messages.filter((message) => isRootMessage(message))),
    [messages],
  )

  const threads = useMemo(() => threadSummaries(messages), [messages])

  /**
   * The first message the reader had not seen when they opened the room, and
   * only when somebody else wrote it: a line above your own message would be
   * telling you about something you just did.
   */
  const firstUnreadSequence = useMemo((): number | null => {
    if (unreadMark === null) return null
    for (const message of messages) {
      if (message.room_sequence <= unreadMark) continue
      if (isOptimisticMessage(message)) return null
      if (message.sender.id === selfId) return null
      return message.room_sequence
    }
    return null
  }, [messages, selfId, unreadMark])

  useEffect(() => {
    if (highlightId === null || onOpenThread === undefined) return
    const target = messages.find((message) => message.id === highlightId)
    if (target !== undefined && !isRootMessage(target) && target.parent_id) {
      onOpenThread(target.parent_id)
    }
  }, [highlightId, messages, onOpenThread])

  const deliveries = useMemo(() => {
    const byClientId = new Map<string, PendingMessage>()
    for (const entry of pending) byClientId.set(entry.client_message_id, entry)
    return byClientId
  }, [pending])

  // ── Scrolling ─────────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    const element = scrollRef.current
    if (element === null) return

    // A different room is not a prepend: its sequences are its own, and the
    // view must start at the bottom rather than trying to hold a position.
    const switched = roomRef.current !== room.id
    if (switched) {
      roomRef.current = room.id
      followingRef.current = true
      initialScrollRoomRef.current = null
      anchorRef.current = { scrollHeight: 0, scrollTop: 0, firstSequence: null }
    }

    const first = messages[0]?.room_sequence ?? null
    const anchor = anchorRef.current
    const prepended =
      !switched && anchor.firstSequence !== null && first !== null && first < anchor.firstSequence

    // A room may first render with no messages and receive its history on the
    // next store tick. Snap to the newest message once the first page arrives;
    // otherwise the browser can leave us at the top after opening a room.
    const initialPageArrived = initialScrollRoomRef.current !== room.id && messages.length > 0

    if (initialPageArrived) {
      element.scrollTop = element.scrollHeight
      initialScrollRoomRef.current = room.id
      followingRef.current = true
    } else if (prepended) {
      // The new content sits above the viewport; push the scroll down by
      // exactly as much as the document grew.
      element.scrollTop = element.scrollHeight - anchor.scrollHeight + anchor.scrollTop
    } else if (followingRef.current) {
      element.scrollTop = element.scrollHeight
    }

    anchorRef.current = {
      scrollHeight: element.scrollHeight,
      scrollTop: element.scrollTop,
      firstSequence: first,
    }
  }, [messages, room.id])

  const onScroll = useCallback(
    (event: UIEvent<HTMLDivElement>): void => {
      const element = event.currentTarget
      const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight
      const atBottom = distanceToBottom <= AT_BOTTOM_THRESHOLD_PX

      followingRef.current = atBottom
      setFollowing((current) => (current === atBottom ? current : atBottom))

      // Kept current so the anchor is measured against where the user actually
      // is, not against where they were when the last render happened.
      anchorRef.current.scrollTop = element.scrollTop
      anchorRef.current.scrollHeight = element.scrollHeight

      if (element.scrollTop <= LOAD_OLDER_THRESHOLD_PX && hasMore && !loading) loadOlder()
    },
    [hasMore, loading, loadOlder],
  )

  // Media and embeds can grow a message after the history has been painted.
  // Keep the initial view pinned to the newest content while the reader is
  // following the conversation; once they scroll up, their position is left
  // untouched.
  useEffect(() => {
    const element = scrollRef.current
    if (element === null || typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
      return undefined
    }

    let frame: number | null = null
    const resize = new ResizeObserver(() => {
      if (!followingRef.current) return
      if (frame !== null) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        element.scrollTop = element.scrollHeight
        frame = null
      })
    })
    const observeChildren = (): void => {
      resize.disconnect()
      for (const child of Array.from(element.children)) resize.observe(child)
    }
    const mutations = new MutationObserver(observeChildren)
    observeChildren()
    mutations.observe(element, { childList: true, subtree: true })

    return () => {
      mutations.disconnect()
      resize.disconnect()
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [])

  // ── Read state ────────────────────────────────────────────────────────────

  const lastReadableSequence = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index]
      if (message !== undefined && !isOptimisticMessage(message)) return message.room_sequence
    }
    return 0
  }, [messages])

  // Read out of `room` here rather than inside the effect: a call in the
  // dependency array cannot be checked statically, and depending on `room`
  // itself would re-run this on every render, since the store hands back a new
  // object each time a room changes at all.
  const { id: roomId } = room
  const lastReadSequence = unreadOf(room).last_read_sequence

  useEffect(() => {
    if (!following) return
    if (lastReadableSequence <= lastReadSequence) return
    void markRoomRead(roomId, lastReadableSequence)
  }, [following, lastReadableSequence, lastReadSequence, roomId])

  // ── Announcements ─────────────────────────────────────────────────────────

  // Declared before the announcer so that a room change clears the marker
  // first, and the newest message of the room just opened is not read out.
  useEffect(() => {
    announcedRef.current = null
    setFollowing(true)
    setEditingId(null)
    setAnnouncement('')
    setUnreadMark(null)
  }, [room.id])

  // Set once per room, from the first read state the store has for it.
  useEffect(() => {
    setUnreadMark((current) => {
      if (current !== null) return current
      return lastReadSequence > 0 ? lastReadSequence : null
    })
  }, [lastReadSequence, roomId])

  useEffect(() => {
    const latest = messages[messages.length - 1]
    if (latest === undefined) return

    // The first render of a room is not an arrival; announcing the whole
    // backlog would be unbearable.
    if (announcedRef.current === null) {
      announcedRef.current = latest.id
      return
    }
    if (announcedRef.current === latest.id) return
    announcedRef.current = latest.id

    if (latest.sender.id === selfId || latest.deleted_at !== null) return
    // A reply is not on this list. Reading it out here would announce a
    // message the user cannot see unless they open the thread.
    if (!isRootMessage(latest)) return
    setAnnouncement(`${senderLabel(latest)}: ${latest.text}`)
  }, [messages, selfId])

  const endEdit = useCallback(() => setEditingId(null), [])

  return (
    <>
      <div
        ref={scrollRef}
        className={styles.scroller}
        onScroll={onScroll}
        role="log"
        aria-label={`Messages in ${roomName}`}
        tabIndex={0}
      >
        {highlightId !== null && jumpTarget === undefined && !loading ? (
          <div className={styles.older} role="status">
            {hasMore ? <><p>The linked message is further back in this conversation.</p>{jumpPages === 0 ? <Button onClick={() => setJumpPages(10)}>Continue finding message</Button> : null}</> : <p>The linked message is not available in this conversation.</p>}
          </div>
        ) : null}
        {hasMore ? (
          <div className={styles.older}>
            {loading ? (
              <Spinner size="sm" label="Loading older messages" />
            ) : (
              <Button size="sm" onClick={loadOlder}>
                Load older messages
              </Button>
            )}
          </div>
        ) : messages.length > 0 ? (
          <p className={styles.start}>This is the beginning of the conversation.</p>
        ) : null}

        {error !== null ? (
          <p className={styles.failure} role="alert">
            {error}
          </p>
        ) : null}

        {messages.length === 0 && !loading ? (
          <EmptyState
            fill
            title="No messages yet"
            description={
              room.kind === 'channel'
                ? `You are first into ${roomName}. Write something, and the channel is under way.`
                : 'Say something to get this started.'
            }
          />
        ) : null}

        {days.map((day) => (
          <Fragment key={day.key}>
            <p className={styles.day}>{day.label}</p>
            {day.groups.map((group) => {
              const lead = group.messages[0]
              if (lead === undefined) return null
              const label = senderLabel(lead)
              const visibleLabel = label

              return (
                <div className={styles.group} key={group.key}>
                  <div className={styles.groupHeader}>
                    <Avatar
                      className={styles.avatar}
                      name={label}
                      id={lead.sender.id}
                      src={lead.sender_avatar_url ?? lead.sender.avatar_url}
                      size={avatarSize}
                      decorative
                    />
                    <div className={styles.groupMeta}>
                      <div className={styles.groupTitle}>
                        {lead.sender_override !== null ? (
                          <span className={styles.sender} title={label}>
                            {visibleLabel}
                          </span>
                        ) : (
                          <UserNameButton
                            user={lead.sender}
                            roomId={room.id}
                            className={styles.senderButton}
                            title={label}
                          >
                            {visibleLabel}
                          </UserNameButton>
                        )}
                      </div>
                      {lead.sender_override !== null ? (
                        <Badge tone="neutral" className={styles.webhook}>
                          webhook
                        </Badge>
                      ) : lead.sender.is_bot ? (
                        <Badge tone="neutral" className={styles.webhook}>
                          bot
                        </Badge>
                      ) : null}
                      {!lead.sender.is_local ? (
                        <span className={styles.serverStamp}>{lead.sender.server_id}</span>
                      ) : null}
                    </div>
                  </div>

                  <div className={styles.groupBody}>
                    {group.messages.map((message, index) => (
                      <Fragment key={message.id}>
                        {unreadMark !== null &&
                        message.room_sequence === firstUnreadSequence ? (
                          <p className={styles.newMessages}>
                            <span>New messages</span>
                          </p>
                        ) : null}
                        <MessageRow
                          message={message}
                          room={room}
                          compact={layout.compact}
                          selfId={selfId}
                          canModerate={canModerate}
                          showStamp={index > 0}
                          highlighted={message.id === highlightId}
                          delivery={
                            message.client_message_id === null
                              ? undefined
                              : deliveries.get(message.client_message_id)
                          }
                          editing={editingId === message.id}
                          onEdit={() => (onEditMessage ? onEditMessage(message) : setEditingId(message.id))}
                          onEditDone={endEdit}
                          onRetry={retry}
                          onDiscard={discard}
                          onReply={
                            onOpenThread === undefined
                              ? undefined
                              : () => onOpenThread(message.parent_id ?? message.id)
                          }
                          thread={threads.get(message.id) ?? NO_THREAD}
                          onOpenThread={
                            onOpenThread === undefined ? undefined : () => onOpenThread(message.id)
                          }
                        />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )
            })}
          </Fragment>
        ))}
      </div>

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
    </>
  )
}
